/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import {
  format,
  parse,
  startOfWeek,
  getDay,
  isBefore,
  startOfDay,
  startOfMonth,
  endOfMonth,
  endOfWeek,
} from 'date-fns';
import { vi } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  Modal,
  Button,
  Form,
  Container,
  Alert,
} from 'react-bootstrap';
import calcomService from '../services/calcom';
import CanhBaoKhacDiaChiModal from '../components/CanhBaoKhacDiaChiModal';
import { khoangGio } from '../utils/dinhDangGio';
import {
  DANH_SACH_CABIN,
  DIA_CHI_CABIN,
  cabinChuaMo,
  canhBaoKhacDiaChi,
  ngayMoCuaCabin,
  timCabinTheoId,
} from '../constants/cabin.constant';
import integrationApi from '../services/integration';
import drivingApi from '../services/driving';
import { toast } from 'react-toastify';
import useMediaQuery from '../hook/useMediaQuery';
import { ROLE } from '../constants/roles.constant';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { vi: vi },
});
const messages = {
  allDay: 'Cả ngày',
  previous: 'Trước',
  next: 'Tiếp',
  today: 'Hôm nay',
  month: 'Tháng',
  week: 'Tuần',
  day: 'Ngày',
  agenda: 'Lịch trình',
  date: 'Ngày',
  time: 'Thời gian',
  event: 'Sự kiện',
  noEventsInRange: 'Không có sự kiện trong khoảng thời gian này.',
  showMore: (total) => `+ Xem thêm ${total} sự kiện`,
};
const BOOKING_STATUS = {
  pending: 'Đang chờ xác nhận',
  accepted: 'Đã đăng ký',
  cancelled: 'Đã hủy',
  absent: 'Vắng mặt',
};
// Students may cancel their own cabin booking up to this many ms BEFORE
// the lesson start. Loosened 2026-06-21 from 24h → 2h based on operator
// feedback that 24h was too strict for last-minute student emergencies
// (sickness, family). Admin/teacher cancels on /admin/cabin are not
// subject to this window — they can cancel anytime before lesson start.
// BE is expected to mirror this window when the gate ships server-side
// (see calcom.controller-policy.js).
const CANCEL_WINDOW_MS = 2 * 60 * 60 * 1000;
const DEFAULT_CABIN_SESSIONS = [
  { label: 'Ca 1', start: '07:30', end: '09:40' },
  { label: 'Ca 2', start: '09:40', end: '11:50' },
  { label: 'Ca 3', start: '11:50', end: '14:00' },
  { label: 'Ca 4', start: '14:00', end: '16:10' },
  { label: 'Ca 5', start: '16:10', end: '18:20' },
  { label: 'Ca 6', start: '18:20', end: '20:30' },
];

const PHU_GIAO_CABIN_SESSIONS = [
  { label: 'Ca 1', start: '07:30', end: '09:30' },
  { label: 'Ca 2', start: '09:45', end: '11:45' },
  { label: 'Ca 3', start: '12:00', end: '14:00' },
  { label: 'Ca 4', start: '14:15', end: '16:15' },
];

const PHU_GIAO_HIDDEN_SLOT_FROM = '2026-07-01';
const isPhuGiaoSlotHidden = (eventId, dateKey, sessionStart) =>
  eventId?.toString() === '4896118' &&
  dateKey >= PHU_GIAO_HIDDEN_SLOT_FROM &&
  sessionStart === '07:30';

const getCabinSessions = (eventId) =>
  eventId?.toString() === '4896118'
    ? PHU_GIAO_CABIN_SESSIONS
    : DEFAULT_CABIN_SESSIONS;

const buildDateFromKeyAndTime = (dateKey, timeValue) => {
  const [year, month, day] = dateKey.split('-').map(Number);
  const [hours, minutes] = timeValue.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes, 0);
};

const CalcomPage = () => {
  const [searchParams] = useSearchParams();
  const [selectedEvent, setSelectedEvent] = useState('');
  // Cabin đang chờ học viên xác nhận đã biết nó ở cơ sở khác. Giữ riêng với
  // `selectedEvent`: chưa xác nhận thì chưa coi như đã chọn, nên nếu học viên
  // bấm "Chọn cabin khác" thì ô chọn quay về trống chứ không kẹt ở cabin sai.
  const [cabinChoXacNhan, setCabinChoXacNhan] = useState('');
  const [events, setEvents] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [loadingRegistration, setLoadingRegistration] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [canceling, setCanceling] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [view, setView] = useState('month');
  const [customerData, setCustomerData] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [manualPhone, setManualPhone] = useState('');
  // Student-mode lockdown. Set true when the page is entered via
  // ?lms_token=<jwt> from lms.uniapp.vn — restricts UI to the JWT's
  // student only and hides CRM admin-only fields.
  const [studentMode, setStudentMode] = useState(false);
  const [studentAccessError, setStudentAccessError] = useState(null);
  // Raw lms_token kept around AFTER verifyCabinToken succeeds. BE-side
  // cancel policy needs it back in the body to verify booking ownership
  // (driving-dat axios bearer is null/teacher-only — BE can't trust it).
  // Never logged, never sent to anywhere except the cancel endpoint.
  const [lmsToken, setLmsToken] = useState(null);

  // Cancel-cabin policy: shared teacher accounts can be abused to drop
  // another teacher's student to free a slot, so cancellation is locked
  // to students (via LMS token) and admins only. BE must enforce the
  // same rule; this gate is just UX/friction.
  const isAdminUser = useMemo(() => {
    try {
      const info = JSON.parse(localStorage.getItem('user-info') || 'null');
      const roles = Array.isArray(info?.role) ? info.role : [];
      return (
        roles.includes(ROLE.ADMIN) ||
        roles.includes(ROLE.DRIVING.ADMIN) ||
        roles.includes(ROLE.DRIVING.DAT_ADMIN)
      );
    } catch (_) {
      return false;
    }
  }, []);
  const cancelRoleAllowed = studentMode || isAdminUser;

  const existingPhone =
    customerData?.mapper?.phone_office?.display_value || '';
  const needsPhoneInput = !existingPhone;

  const normalizePhone = (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return '';
    return trimmed.startsWith('0') ? '+84' + trimmed.slice(1) : trimmed;
  };

  const isValidVnPhone = (value) => /^(0|\+84)\d{9}$/.test((value || '').trim());

  useEffect(() => {
    setView(isMobile ? 'day' : 'month');
  }, [isMobile]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const accountId = searchParams.get('account_id');
    const registrationCode = searchParams.get('registration_code');
    const lmsToken = searchParams.get('lms_token');
    if (lmsToken) {
      // LMS-driven entry (student self-service). Verify the JWT with
      // uniapp-backend before letting them through. JWT carries
      // drivingStudentId + registrationCode + name signed with
      // ELEARNING_SESSION_TOKEN_SECRET — no other student is reachable
      // because every booking lookup is keyed by the verified
      // registrationCode below.
      (async () => {
        try {
          setLoading(true);
          const { data } = await drivingApi.verifyCabinToken(lmsToken);
          const claim = data || {};
          if (!claim.registrationCode || !claim.drivingStudentId) {
            throw new Error('Token thiếu thông tin học viên');
          }
          setStudentMode(true);
          setLmsToken(lmsToken);
          // BE verifyCabinToken kèm customerSnapshot { phone, ngayHocHih, name }
          // được lookup server-side từ GetFly (BE-internal, không lộ endpoint).
          // Merge vào mapper theo đúng shape integrationApi.getCustomerDetail
          // trả về cho teacher flow — downstream code dùng chung field name
          // (phone_office, ngay_hoc_hih, account_name).
          const snap = claim.customerSnapshot || null;
          setCustomerData({
            mapper: {
              account_name: { display_value: snap?.name || claim.name || '' },
              ma_hoc_vien_theo_csdl: {
                field_value: claim.registrationCode,
                display_value: claim.registrationCode,
              },
              ...(snap?.phone ? {
                phone_office: { display_value: snap.phone, field_value: snap.phone },
              } : {}),
              ...(snap?.ngayHocHih ? {
                ngay_hoc_hih: { field_value: snap.ngayHocHih, display_value: snap.ngayHocHih },
              } : {}),
            },
            drivingStudentId: claim.drivingStudentId,
            studentMode: true,
          });
          await getElearningStatus(claim.registrationCode);
        } catch (error) {
          console.error('verifyCabinToken failed:', error);
          setStudentAccessError(
            error?.response?.data?.message
            || 'Liên kết đăng ký không hợp lệ hoặc đã hết hạn. Vui lòng quay lại trang Kết quả học và bấm "Đăng ký lịch cabin" lại.'
          );
        } finally {
          setLoading(false);
        }
      })();
    } else if (accountId) {
      // CRM-driven entry (teacher / admin tool) — full customer detail.
      fetchCustomerData(accountId);
    } else if (registrationCode) {
      // Legacy entry: registration_code without lms_token. Treat as
      // teacher tool (no studentMode lockdown) since there's no signed
      // proof of student identity. CRM data won't be available but the
      // existing booking guard still applies.
      setCustomerData({
        mapper: { ma_hoc_vien_theo_csdl: { field_value: registrationCode } },
      });
      getElearningStatus(registrationCode);
    }
  }, [searchParams]);

  const fetchCustomerData = async (accountId) => {
    try {
      setLoading(true);
      const data = await integrationApi.getCustomerDetail(accountId);
      if (data) {
        setCustomerData(data);
        // Fetch elearning status if registration code is available
        if (data.mapper?.['ma_hoc_vien_theo_csdl']?.field_value) {
          getElearningStatus(
            data.mapper['ma_hoc_vien_theo_csdl'].field_value,
            data.mapper?.['can_cuoc_cong_dan']?.field_value,
            data.mapper?.['ma_khoa_csdl']?.field_value,
          );
        }
      } else {
        toast.error('Không tìm thấy thông tin học viên.');
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      toast.error('Lỗi khi tải thông tin học viên.');
    } finally {
      setLoading(false);
    }
  };

  const getElearningStatus = async (registrationCode, cardNumber, courseCode) => {
    try {
      setLoading(true);
      const { data } = await drivingApi.getElearningStatusByRegistrationCode(registrationCode, cardNumber, courseCode);
      setCustomerData(prev => ({
        ...prev,
        elearningStatus: data?.elearningStatus || 'created',
        elearningLastReportAt: data?.lastReportAt || null,
        calBookingId: data?.calBookingId || null,
        calBookingUid: data?.calBookingUid || null,
      }));
      if (data?.calBookingUid) {
        fetchBooking(data.calBookingUid);
      }
    } catch (error) {
      console.error('Error fetching elearning status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBooking = async (bookingId, serviceKey) => {
    setLoadingRegistration(true);
    try {
      setLoading(true);
      const response = await calcomService.getBooking(bookingId, serviceKey);
      setBookingData(response.data);
    } catch (error) {
      console.error('Error fetching booking:', error);
    } finally {
      setLoading(false);
      setLoadingRegistration(false);
    }
  };

  // Danh sách cabin nay ở constants/cabin.constant — dùng chung với
  // AdminCalcomPage. Trước đây mỗi trang giữ một bản chép tay.
  const eventOptions = DANH_SACH_CABIN;

  const getEventConfigById = timCabinTheoId;

  const getEventConfigByBookingData = (booking) => {
    const eventTypeId = booking?.data?.eventTypeId || booking?.eventTypeId;
    return getEventConfigById(eventTypeId);
  };

  const cabinAddresses = DIA_CHI_CABIN;

  const fetchSlots = async (eventId, date) => {
    setLoading(true);
    try {
      const cabinSessions = getCabinSessions(eventId);
      const selectedEventConfig = getEventConfigById(eventId);
      const startOfMonthDate = startOfMonth(date);
      const endOfMonthDate = endOfMonth(date);
      const startTime = startOfWeek(startOfMonthDate, { weekStartsOn: 1 }).toISOString();
      const endTime = endOfWeek(endOfMonthDate, { weekStartsOn: 1 }).toISOString();
      const slotResponse = await calcomService.getAvailableSlots(
        eventId,
        startTime,
        endTime,
        { serviceKey: selectedEventConfig?.serviceKey },
      );
      const slotsData = slotResponse.data.data || {};

      const personalBooking = bookingData?.data || bookingData;
      const personalStatus = personalBooking?.status;
      const personalEventTypeId = personalBooking?.eventTypeId?.toString();
      const isPersonalBookingActive =
        !!personalBooking &&
        !!personalStatus &&
        personalStatus !== 'cancelled' &&
        personalStatus !== 'rejected' &&
        personalEventTypeId === eventId?.toString();

      const personalBookingKey = (() => {
        if (!isPersonalBookingActive) return null;
        const personalStart = new Date(personalBooking.start);
        const personalEnd = new Date(personalBooking.end);
        if (Number.isNaN(personalStart.getTime()) || Number.isNaN(personalEnd.getTime())) {
          return null;
        }
        const dateKey = format(personalStart, 'yyyy-MM-dd');
        const timeKey = `${format(personalStart, 'HH:mm')}-${format(personalEnd, 'HH:mm')}`;
        return `${dateKey}|${timeKey}`;
      })();

      const personalBookingDateKey = personalBookingKey
        ? personalBookingKey.split('|')[0]
        : null;

      const allDateKeys = Array.from(
        new Set([
          ...Object.keys(slotsData),
          ...(personalBookingDateKey ? [personalBookingDateKey] : []),
        ]),
      ).sort();

      const calendarEvents = [];
      allDateKeys.forEach((date) => {
        const slotsByTimeKey = new Map(
          (slotsData[date] || []).map((slot) => {
            const start = new Date(slot.start);
            const end = new Date(slot.end);
            const key = `${format(start, 'HH:mm')}-${format(end, 'HH:mm')}`;
            return [key, slot];
          }),
        );

        // Cabin chưa tới ngày mở thì bỏ qua CẢ NGÀY, không đẩy ca nào vào lịch.
        // Đặt ngoài vòng lặp ca vì mốc này tính theo ngày, không theo từng ca.
        if (cabinChuaMo(eventId, date)) return;

        cabinSessions.forEach((session) => {
          if (isPhuGiaoSlotHidden(eventId, date, session.start)) return;
          const slotKey = `${session.start}-${session.end}`;
          const matchedSlot = slotsByTimeKey.get(slotKey);
          const isOwnBooking = personalBookingKey === `${date}|${slotKey}`;
          const start = matchedSlot
            ? new Date(matchedSlot.start)
            : buildDateFromKeyAndTime(date, session.start);
          const end = matchedSlot
            ? new Date(matchedSlot.end)
            : buildDateFromKeyAndTime(date, session.end);

          calendarEvents.push({
            title: matchedSlot
              ? `${session.label}: ${session.start} - ${session.end}`
              : isOwnBooking
                ? `${session.label}: Lịch của bạn`
                : `${session.label}: X`,
            start,
            end,
            resource: matchedSlot || null,
            isUnavailable: !matchedSlot,
            isPersonalBooking: isOwnBooking,
          });
        });
      });
      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error fetching slots:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEventChange = async (eventId) => {
    // Cabin ngoài cơ sở chính thì CHẶN ở đây, trước khi tải lịch: bắt xác nhận
    // sớm để học viên không mất công chọn ca rồi mới biết mình nhầm chỗ.
    if (canhBaoKhacDiaChi(eventId)) {
      setCabinChoXacNhan(eventId);
      return;
    }
    setSelectedEvent(eventId);
    if (eventId) {
      await fetchSlots(eventId, currentDate);
    } else {
      setEvents([]);
    }
  };

  /** Học viên đã đọc và xác nhận — giờ mới thật sự chọn cabin đó. */
  const xacNhanKhacDiaChi = async () => {
    const id = cabinChoXacNhan;
    setCabinChoXacNhan('');
    setSelectedEvent(id);
    await fetchSlots(id, currentDate);
  };

  /**
   * Không xác nhận → chỉ đóng modal. `selectedEvent` chưa từng đổi, nên ô chọn
   * tự quay về cabin đang chọn trước đó. Xoá trắng ở đây là phạt học viên vì
   * đã lỡ mở thử một cabin.
   */
  const boCabinKhacDiaChi = () => setCabinChoXacNhan('');

  const handleSelectAvailableEvent = (calendarEvent) => {
    if (calendarEvent?.isUnavailable || !calendarEvent?.resource) {
      toast.info('Ca học này hiện không còn trống.');
      return;
    }

    const selectedDate = calendarEvent?.start;
    if (!selectedDate) {
      return;
    }

    const today = startOfDay(new Date());
    const startDateValue =
      customerData?.mapper?.['ngay_hoc_hih']?.field_value || '';
    const startDate = startDateValue
      ? parse(startDateValue, 'dd/MM/yyyy', new Date())
      : null;

    if (!startDateValue) {
      toast.error('Học viên chưa có dữ liệu ngày được phép đăng ký cabin. Vui lòng liên hệ trung tâm để cập nhật thông tin.');
      return;
    }

    if (isBefore(selectedDate, today)) {
      return; // Disabled
    }

    if (
      startDate &&
      !isNaN(startDate) &&
      isBefore(selectedDate, startOfDay(startDate))
    ) {
      toast.error(`Học viên chỉ được đăng ký Cabin từ ngày ${startDateValue} trở đi.`);
      return;
    }

    setSelectedSlot(calendarEvent.resource);
    setShowConfirmModal(true);
  };

  const handleConfirmBooking = async () => {
    await handleBooking();
  };

  const getBookingUid = () =>
    bookingData?.data?.uid ||
    bookingData?.data?.bookingUid ||
    bookingData?.uid ||
    customerData?.calBookingUid ||
    '';

  const getBookingStartMs = () => {
    const start = bookingData?.data?.start || bookingData?.start;
    const startMs = start ? new Date(start).getTime() : NaN;
    return Number.isNaN(startMs) ? null : startMs;
  };

  const getStudentCancelState = () => {
    const status = bookingData?.data?.status;
    if (!bookingData || !status) return { canCancel: false, reason: '' };
    if (status === 'cancelled') {
      return { canCancel: false, reason: 'Lịch đã được hủy.' };
    }
    const startMs = getBookingStartMs();
    if (!startMs) {
      return { canCancel: false, reason: 'Không xác định được thời gian học.' };
    }
    const remainingMs = startMs - Date.now();
    if (remainingMs < CANCEL_WINDOW_MS) {
      return {
        canCancel: false,
        reason: 'Chỉ được hủy lịch chậm nhất trước 2 giờ so với thời gian học.',
      };
    }
    return { canCancel: true, reason: '' };
  };

  const handleConfirmCancel = async () => {
    if (!cancelRoleAllowed) {
      toast.error('Tài khoản giáo viên không được phép huỷ lịch cabin.');
      return;
    }
    const bookingUid = getBookingUid();
    if (!bookingUid) {
      toast.error('Không tìm thấy mã booking để hủy.');
      return;
    }
    if (!cancelReason.trim()) {
      toast.error('Vui lòng nhập lý do hủy.');
      return;
    }
    const { canCancel, reason } = getStudentCancelState();
    if (!canCancel) {
      toast.error(reason || 'Không thể hủy lịch cabin.');
      return;
    }

    setCanceling(true);
    try {
      const bookingEventConfig = getEventConfigByBookingData(bookingData);
      await calcomService.cancelBooking(
        bookingUid,
        cancelReason.trim(),
        bookingEventConfig?.serviceKey,
        // Only pass lms_token when we're in studentMode — admins use
        // their Bearer JWT and don't need (or have) one.
        studentMode ? lmsToken : undefined,
      );
      toast.success('Đã hủy lịch cabin.');
      setShowCancelModal(false);
      setCancelReason('');
      await fetchBooking(bookingUid, bookingEventConfig?.serviceKey);
    } catch (error) {
      console.error('Cancel booking error:', error);
      // BE trả 403 kèm lý do cụ thể (không sở hữu lịch / dưới 2h / không
      // xác minh được) — hiện nguyên văn để học viên biết vì sao thay vì
      // thông báo chung chung.
      toast.error(error?.response?.data?.message || 'Hủy lịch cabin thất bại.');
    } finally {
      setCanceling(false);
    }
  };

  const handleBooking = async () => {
    if (!selectedSlot) return;

    const phoneSource = existingPhone || manualPhone;
    if (!phoneSource) {
      toast.error('Vui lòng nhập số điện thoại liên hệ.');
      return;
    }
    if (!isValidVnPhone(phoneSource)) {
      toast.error('Số điện thoại không hợp lệ. Vui lòng nhập số có 10 chữ số.');
      return;
    }

    setRegistering(true);
    try {
      const phoneNumber = normalizePhone(phoneSource);
      const bookingData = {
        serviceKey: getEventConfigById(selectedEvent)?.serviceKey,
        start: new Date(selectedSlot.start).toISOString(),
        eventTypeId: parseInt(selectedEvent),
        ...(customerData && {
          attendee: {
            name: customerData.mapper?.account_name?.display_value,
            phoneNumber: phoneNumber,
            timeZone: 'Asia/Ho_Chi_Minh',
            language: 'vi'
          },
          // In studentMode the LMS JWT carries only name + reg code, so
          // CRM-only fields (account_id, birthday, cccd, khoa_hoc) are
          // omitted from the booking metadata. The driving_student_id
          // claim from the JWT is the canonical ownership marker.
          metadata: studentMode ? {
            ma_hoc_vien: customerData.mapper?.ma_hoc_vien_theo_csdl?.display_value,
            ten_hoc_vien: customerData.mapper?.account_name?.display_value,
            driving_student_id: customerData.drivingStudentId,
            source: 'lms',
          } : {
            account_id: customerData.account_id,
            ma_hoc_vien: customerData.mapper?.ma_hoc_vien_theo_csdl?.display_value,
            ten_hoc_vien: customerData.mapper?.account_name?.display_value,
            ngay_sinh: customerData.mapper?.birthday?.display_value,
            cccd: customerData.mapper?.can_cuoc_cong_dan?.display_value,
            khoa_hoc: customerData.mapper?.khoa_hoc?.display_value,
          }
        })
      };
      // Pass lms_token only in studentMode so BE checkCreatePolicy can
      // run the double-booking guard against the student's drivingId.
      // Teacher/admin flow stays Bearer-only.
      const response = await calcomService.createBooking(
        bookingData,
        studentMode ? lmsToken : undefined,
      );
      await fetchBooking(
        response?.data?.data?.uid,
        getEventConfigById(selectedEvent)?.serviceKey,
      );
      toast.success('Đăng ký thành công!');
      setShowConfirmModal(false);
      // Reset
      setSelectedEvent('');
      setEvents([]);
      setSelectedSlot(null);
      setManualPhone('');
    } catch (error) {
      console.error('Error creating booking:', error);
      // BE returns 409 with a localized reason when the double-booking
      // guard fires ("Bạn đã có lịch cabin đang hoạt động..."). Surface
      // that message verbatim instead of the generic toast so the
      // student knows they need to cancel the existing booking first.
      const msg =
        error?.response?.status === 409
          ? error?.response?.data?.message || 'Đăng ký thất bại!'
          : 'Đăng ký thất bại!';
      toast.error(msg);
    } finally {
      setRegistering(false);
    }
  };

  const isBookingAbsent = Boolean(
    bookingData?.data?.attendees?.some((attendee) => attendee?.absent) ||
      bookingData?.data?.absent,
  );

  // Check if previous booking's scheduled time has already passed
  const isPreviousBookingTimePassed = (() => {
    if (!bookingData?.data?.start) return false;
    const bookingStartTime = new Date(bookingData.data.start).getTime();
    const nowTime = Date.now();
    return bookingStartTime < nowTime;
  })();

  return (
    <Container className='mt-4'>
      <h1 className='text-center mb-4'>Đăng ký Cabin điện tử</h1>

      {studentAccessError && (
        <Alert variant='danger' className='mb-3'>
          <strong>Không thể đăng ký:</strong> {studentAccessError}
        </Alert>
      )}

      {customerData && !studentAccessError && (
        <Alert variant='info' className='mb-3'>
          <p>
            <strong>Họ và tên:</strong>{' '}
            {customerData.mapper?.account_name?.display_value}
          </p>
          <p>
            <strong>Mã học viên:</strong>{' '}
            {customerData.mapper?.ma_hoc_vien_theo_csdl?.display_value}
          </p>
          {!studentMode && (
            <>
              {/* CRM-only fields. Hidden in student mode because the LMS
                  JWT doesn't carry CRM data and showing blank fields is
                  worse than hiding them entirely. */}
              <p>
                <strong>Ngày sinh:</strong>{' '}
                {customerData.mapper?.birthday?.display_value}
              </p>
              <p>
                <strong>Số CMND/CCCD:</strong>{' '}
                {customerData.mapper?.can_cuoc_cong_dan?.display_value}
              </p>
              <p>
                <strong>Khóa học:</strong>{' '}
                {customerData.mapper?.khoa_hoc?.display_value}
              </p>
            </>
          )}
          <p>
            <strong>Trạng thái học lý thuyết: </strong>
            <span
              className={
                customerData.elearningStatus === 'completed'
                  ? 'text-success'
                  : 'text-warning'
              }
            >
              {customerData.elearningStatus === 'completed'
                ? 'Đã hoàn thành'
                : loading
                  ? 'Đang tải...'
                  : 'Chưa hoàn thành'}
            </span>
          </p>
        </Alert>
      )}

      {bookingData ? (
        <Alert variant='success' className='mb-3'>
          {(() => {
            const { canCancel } = getStudentCancelState();
            const bookingStatus = bookingData?.data?.status;
            const bookingStatusLabel = isBookingAbsent
              ? BOOKING_STATUS.absent
              : BOOKING_STATUS[bookingStatus];
            const bookingStatusClass = bookingStatus === 'cancelled'
              ? 'text-danger fw-bold'
              : '';
            return (
              <>
                <h5 className='mb-3'>Thông tin đăng ký</h5>
                <p>
                  <strong>Máy cabin:</strong>{' '}
                  {
                    eventOptions.find(
                      (e) => e.id === bookingData?.data?.eventTypeId?.toString(),
                    )?.name || 'Không xác định'
                  }
                </p>
                <p>
                  <strong>Thời gian:</strong>{' '}
                  {khoangGio(bookingData.data.start, bookingData.data.end)}
                </p>
                <p>
                  <strong>Địa chỉ:</strong> {bookingData.data.location}
                </p>
                <p>
                  <strong>Trạng thái:</strong>{' '}
                  <span className={bookingStatusClass}>
                    {bookingStatusLabel}
                  </span>
                </p>
                <div>
                  {cancelRoleAllowed ? (
                    canCancel ? (
                      <>
                        <Button
                          variant='outline-danger'
                          size='sm'
                          onClick={() => setShowCancelModal(true)}
                        >
                          Hủy lịch
                        </Button>
                        <p className='text-danger small mt-2 mb-0'>
                          Lưu ý: Chỉ được phép hủy lịch chậm nhất trước 2 giờ so
                          với thời gian học.
                        </p>
                      </>
                    ) : null
                  ) : (
                    <p className='text-muted small mt-2 mb-0'>
                      Tài khoản giáo viên không được phép huỷ lịch cabin. Học viên
                      vui lòng huỷ qua trang LMS, hoặc liên hệ admin trung tâm nếu
                      cần điều chỉnh.
                    </p>
                  )}
                </div>
              </>
            );
          })()}
        </Alert>
      ) : loadingRegistration ? (
        <Alert variant='info' className='mb-3'>
          Đang tải thông tin đăng ký cabin...
        </Alert>
      ) : null}

      {(!bookingData?.data?.status || bookingData?.data?.status !== 'accepted' || isBookingAbsent || isPreviousBookingTimePassed) && 
      (
        <>
          <Form.Group className='mb-3'>
            <Form.Label>Chọn máy cabin:</Form.Label>
            <Form.Select
              value={selectedEvent}
              onChange={(e) => handleEventChange(e.target.value)}
              disabled={
                !customerData || customerData.elearningStatus !== 'completed'
              }
            >
              <option value=''>Chọn máy cabin</option>
              {eventOptions.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <CanhBaoKhacDiaChiModal
            cabinId={cabinChoXacNhan}
            onXacNhan={xacNhanKhacDiaChi}
            onBo={boCabinKhacDiaChi}
          />

          {/* Lịch trống trơn mà không nói lý do thì học viên sẽ gọi lên trung
              tâm hỏi. Một dòng ở đây rẻ hơn một cuộc điện thoại. */}
          {selectedEvent && ngayMoCuaCabin(selectedEvent) && (
            <div className='alert alert-info py-2 px-3 mb-3'>
              Cabin này bắt đầu nhận lịch từ ngày{' '}
              <strong>{ngayMoCuaCabin(selectedEvent)}</strong>. Các ngày trước đó
              chưa mở ca.
            </div>
          )}

          {selectedEvent && cabinAddresses[selectedEvent] && (
            <div className='mb-4'>
              <p className='fw-bold mb-2'>Địa chỉ cabin:</p>
              <p className='mb-0'>{cabinAddresses[selectedEvent]}</p>
            </div>
          )}

          <div style={{ position: 'relative' }} className='mb-5'>
            {customerData?.elearningStatus !== 'completed' ? (
              <p className='text-warning'>
                Học viên chưa hoàn thành học lý thuyết, vui lòng hoàn thành khóa học để đăng ký cabin.
              </p>
            ) : <p className='text-success fw-bold '>
              BẤM trực tiếp vào khung giờ màu xanh để đăng ký.
            </p>}
            <div className='mb-3 p-2 border rounded bg-white'>
              <div className='d-flex flex-wrap gap-3 small'>
                <div className='d-flex align-items-center gap-2'>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      backgroundColor: '#3174ad',
                      borderRadius: 3,
                      display: 'inline-block',
                    }}
                  />
                  <span>Ca học còn trống</span>
                </div>
                <div className='d-flex align-items-center gap-2'>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      backgroundColor: '#6c757d',
                      borderRadius: 3,
                      display: 'inline-block',
                    }}
                  />
                  <span>Ca học không trống (X)</span>
                </div>
                <div className='d-flex align-items-center gap-2'>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      backgroundColor: 'lightgreen',
                      borderRadius: 3,
                      display: 'inline-block',
                    }}
                  />
                  <span>Ngày có ít nhất 1 ca trống</span>
                </div>
                <div className='d-flex align-items-center gap-2'>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      backgroundColor: '#f0f0f0',
                      borderRadius: 3,
                      display: 'inline-block',
                      border: '1px solid #d9d9d9',
                    }}
                  />
                  <span>Ngày đã qua</span>
                </div>
              </div>
            </div>
            {loading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}
              >
                <Alert variant='info'>Đang tải dữ liệu...</Alert>
              </div>
            )}
            {
              selectedEvent && customerData?.elearningStatus === 'completed' &&
              <Calendar
                className='calcom-calendar'
                localizer={localizer}
                culture='vi'
                messages={messages}
                views={['day', 'week', 'month']}
                drilldownView={null}
                showAllEvents
                events={events}
                startAccessor='start'
                endAccessor='end'
                min={new Date(1970, 1, 1, 7, 0, 0)}
                max={new Date(1970, 1, 1, 19, 0, 0)}
                onSelectEvent={handleSelectAvailableEvent}
                onDrillDown={() => null}
                date={currentDate}
                view={view}
                onView={setView}
                style={{ height: view === 'month' ? 1100 : 700 }}
                onNavigate={async (date) => {
                  if (loading) return;
                  setCurrentDate(date);
                  if (selectedEvent) {
                    await fetchSlots(selectedEvent, date);
                  }
                }}
                eventPropGetter={(event) => ({
                  style: {
                    backgroundColor: event.isPersonalBooking
                      ? '#fd7e14'
                      : event.isUnavailable
                        ? '#6c757d'
                        : undefined,
                    borderColor: event.isPersonalBooking
                      ? '#fd7e14'
                      : event.isUnavailable
                        ? '#6c757d'
                        : undefined,
                    cursor: event.isUnavailable ? 'not-allowed' : 'pointer',
                  },
                })}
                dayPropGetter={(date) => {
                  const today = startOfDay(new Date());
                  const isPast = isBefore(date, today);
                  const hasSlots = events.some(
                    (event) =>
                      event.start.toDateString() === date.toDateString() &&
                      !event.isUnavailable,
                  );
                  return {
                    style: {
                      backgroundColor: hasSlots
                        ? 'lightgreen'
                        : isPast
                          ? '#f0f0f0'
                          : 'white',
                      pointerEvents: isPast ? 'none' : 'auto',
                      opacity: isPast ? 0.5 : 1,
                    },
                  };
                }}
              />
            }
          </div>
        </>
      )}

      <Modal
        show={showConfirmModal}
        backdrop={registering ? 'static' : true}
        keyboard={!registering}
        onHide={() => {
          if (registering) return;
          setShowConfirmModal(false);
          setSelectedSlot(null);
        }}
      >
        <Modal.Header closeButton={!registering}>
          <Modal.Title>Xác nhận đăng ký</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>Học viên:</strong>{' '}
            {customerData?.mapper?.account_name?.display_value}
          </p>
          <p>
            <strong>Mã học viên:</strong>{' '}
            {customerData?.mapper?.ma_hoc_vien_theo_csdl?.display_value}
          </p>
          <p>
            <strong>Máy cabin:</strong>{' '}
            {timCabinTheoId(selectedEvent)?.name}
          </p>
          {/* Nhắc lại địa chỉ ở đúng khoảnh khắc cam kết. Cabin ngoài cơ sở
              chính thì làm nổi lên — học viên đã xác nhận ở bước chọn, nhưng
              giữa lúc đó và lúc này họ còn đi chọn ngày, chọn ca. */}
          <p className={canhBaoKhacDiaChi(selectedEvent) ? 'p-2 rounded' : ''}
             style={canhBaoKhacDiaChi(selectedEvent) ? { background: '#fff4e5' } : undefined}>
            <strong>Địa chỉ:</strong>{' '}
            {DIA_CHI_CABIN[selectedEvent] || 'Chưa xác định'}
          </p>
          <p>
            <strong>Thời gian:</strong>{' '}
            {selectedSlot
              ? khoangGio(selectedSlot.start, selectedSlot.end)
              : ''}
          </p>
          {needsPhoneInput && (
            <Form.Group className='mb-3'>
              <Form.Label>
                <strong>Số điện thoại liên hệ</strong>{' '}
                <span className='text-danger'>*</span>
              </Form.Label>
              <Form.Control
                type='tel'
                placeholder='Ví dụ: 0901234567'
                value={manualPhone}
                onChange={(e) => setManualPhone(e.target.value)}
                disabled={registering}
                isInvalid={!!manualPhone && !isValidVnPhone(manualPhone)}
              />
              <Form.Text className='text-muted'>
                Hệ thống chưa có số điện thoại của bạn. Vui lòng nhập để tiếp
                tục đăng ký.
              </Form.Text>
              <Form.Control.Feedback type='invalid'>
                Số điện thoại không hợp lệ.
              </Form.Control.Feedback>
            </Form.Group>
          )}
          <p className='text-warning'>
            Bạn có chắc chắn muốn đăng ký giờ học này?
          </p>
          <div className='text-danger'>
            <p className='mb-1'>Lưu ý:</p>
            <ul className='mb-0'>
              <li>
                Học viên tham gia học Cabin vui lòng đến sớm trước 10 phút so
                với thời gian đã đăng ký.
              </li>
              <li>
                Trường hợp Huỷ / Đổi lịch cần thông báo cho Giáo viên phụ
                trách ít nhất 2 giờ và Huỷ trên phần mềm.
              </li>
            </ul>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => setShowConfirmModal(false)}
            disabled={registering}
          >
            Hủy
          </Button>
          <Button
            variant='primary'
            onClick={handleConfirmBooking}
            disabled={
              registering || (needsPhoneInput && !isValidVnPhone(manualPhone))
            }
          >
            {registering ? 'Đang đăng ký...' : 'Xác nhận đăng ký'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Xác nhận hủy lịch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='mb-2'>Bạn chắc chắn muốn hủy lịch cabin đã đăng ký?</p>
          <Form.Group>
            <Form.Label>Lý do hủy (bắt buộc)</Form.Label>
            <Form.Control
              as='textarea'
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </Form.Group>
          <p className='text-danger mt-2 mb-0'>
            Lưu ý: Chỉ được hủy lịch chậm nhất trước 2 giờ so với thời gian học.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            onClick={() => setShowCancelModal(false)}
            disabled={canceling}
          >
            Đóng
          </Button>
          <Button
            variant='danger'
            onClick={handleConfirmCancel}
            disabled={canceling || !cancelReason.trim()}
          >
            {canceling ? 'Đang hủy...' : 'Xác nhận hủy'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CalcomPage;
